import React from 'react';
import PropTypes from 'prop-types';

import Button from '@decisiv/ui-components/lib/components/Button';
import Tooltip from '@decisiv/ui-components/lib/components/Tooltip';
import { H3, H4, P } from '@decisiv/ui-components/lib/components/Typography';
import UserPlus from '@decisiv/iconix/lib/components/UserPlus';
import UserTimes from '@decisiv/iconix/lib/components/UserTimes';

import roleTag from '../../../shared/roleTag';
import { TagRow } from '../../../shared/styles';
import { ACCOUNTS_SECTION, LOCKS } from './copy';
import {
  AccountIdentity,
  AccountRow,
  AccountRowActions,
  AccountsList,
  LockedControl,
  Section,
  SectionHeader,
  SectionHeading,
} from './styles';

/**
 * The Accounts half of the tab (PD-4069): every account the user holds a role
 * in, and the button that revokes or restores it.
 *
 * Section, SectionHeader and SectionHeading are PermissionsSection's, so the
 * two halves cannot drift into two headings of different sizes — the design
 * draws them identically (Figma 1153:21094 and 1153:21170).
 *
 * The design groups these rows under team headers ("Amazon", "Team B"). Not
 * built: teams do not exist yet (PD-4213) and UserAccessPermissions puts every
 * account in one synthetic group, so the headers would all read "All Accounts".
 */
function AccountAccessTable({
  canActOn,
  canEdit,
  accounts,
  onAdd,
  onRemove,
  onRestore,
}) {
  /**
   * Primary for Restore, secondary for Remove: the design gives the restore the
   * emphasis because a removed row is the one state on this list an admin is
   * being asked to resolve.
   *
   * Remove is disabled on the user's last active account, and says why. Without
   * the tooltip the button is simply inert, and the rule it is enforcing lives
   * in two places the admin cannot see (canActOn here, last_active_account? on
   * the server). Wrapped for the same reason GuardedCheckbox wraps its input: a
   * disabled control fires neither hover nor focus, so a Tooltip pointed
   * straight at one never opens.
   */
  const accessButton = (row, isRemoved) => {
    const blocked = !isRemoved && !canActOn(row);

    const button = (
      <Button
        disabled={blocked}
        icon={isRemoved ? UserPlus : UserTimes}
        kind={isRemoved ? 'primary' : 'secondary'}
        onClick={() => (isRemoved ? onRestore(row) : onRemove(row))}
        size="small"
        text={isRemoved ? ACCOUNTS_SECTION.restore : ACCOUNTS_SECTION.remove}
      />
    );

    if (!blocked) {
      return button;
    }

    return (
      <Tooltip
        placement="top"
        target={
          <LockedControl
            aria-label={`${ACCOUNTS_SECTION.remove} — ${LOCKS.lastAccount}`}
            tabIndex={0}
          >
            {button}
          </LockedControl>
        }
      >
        {LOCKS.lastAccount}
      </Tooltip>
    );
  };

  return (
    <Section>
      <SectionHeader>
        <SectionHeading>
          <H3 style={{ margin: 0 }}>{ACCOUNTS_SECTION.title}</H3>
          <P shade={1} size="small" style={{ margin: 0 }}>
            {ACCOUNTS_SECTION.subtitle}
          </P>
        </SectionHeading>

        {canEdit && (
          <Button
            kind="secondary"
            onClick={onAdd}
            text={ACCOUNTS_SECTION.add}
          />
        )}
      </SectionHeader>

      <AccountsList>
        {accounts.map((row) => {
          const isRemoved = Boolean(row.removedAt);

          return (
            <AccountRow key={row.id} $removed={isRemoved}>
              <AccountIdentity>
                <H4 style={{ margin: 0 }}>{row.name}</H4>
                <P shade={1} size="small" style={{ margin: 0 }}>
                  {ACCOUNTS_SECTION.meta(row.decisivId, row.dealerType)}
                </P>
              </AccountIdentity>

              <AccountRowActions>
                <TagRow>
                  {row.roles.map((role) => (
                    <React.Fragment key={role}>{roleTag(role)}</React.Fragment>
                  ))}
                </TagRow>

                {canEdit && accessButton(row, isRemoved)}
              </AccountRowActions>
            </AccountRow>
          );
        })}
      </AccountsList>
    </Section>
  );
}

AccountAccessTable.propTypes = {
  canActOn: PropTypes.func.isRequired,
  canEdit: PropTypes.bool,
  onAdd: PropTypes.func,
  onRemove: PropTypes.func.isRequired,
  onRestore: PropTypes.func.isRequired,
  accounts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      decisivId: PropTypes.string,
      name: PropTypes.string,
      dealerType: PropTypes.string,
      roles: PropTypes.arrayOf(PropTypes.string),
      removedAt: PropTypes.string,
    }),
  ),
};

AccountAccessTable.defaultProps = {
  canEdit: false,
  onAdd: () => {},
  accounts: [],
};

export default AccountAccessTable;
